import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import {
  TrendingUp, Users, Building2, Calendar, CheckSquare,
  ShoppingCart, ArrowRight, Loader2, RefreshCw,
  Phone, Mail, Target, Activity, Package,
  Clock, AlertCircle, Zap, Truck, Database, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';

// ── Constants ──────────────────────────────────────────────────────────────────
const STAGE_LABELS = {
  new_lead: 'Nuovo Lead', qualified: 'Qualificato', proposal: 'Proposta',
  negotiation: 'Trattativa', won: 'Vinto', lost: 'Perso',
};
const STAGE_COLORS = {
  new_lead: '#6366f1', qualified: '#3b82f6', proposal: '#f59e0b',
  negotiation: '#f97316', won: '#22c55e', lost: '#ef4444',
};
const EVENT_COLORS = {
  appointment: '#6366f1', visit: '#22c55e', call: '#3b82f6',
  followup: '#f59e0b', demo: '#a855f7', meeting: '#f97316',
};
const PRIORITY_COLORS = { low: '#3b82f6', medium: '#06b6d4', high: '#f97316', urgent: '#ef4444' };
const PRIORITY_LABELS = { low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
const EVENT_TYPE_LABELS = {
  appointment: 'Appuntamento', visit: 'Visita', call: 'Chiamata',
  followup: 'Follow-up', demo: 'Demo', meeting: 'Riunione',
};
const ACT_TYPE_ICONS = { call: Phone, email: Mail, todo: CheckSquare, followup: ArrowRight, meeting_prep: Users };
const ORDER_STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#3b82f6', shipped: '#a855f7',
  delivered: '#22c55e', cancelled: '#ef4444', invoiced: '#06b6d4',
};
const ORDER_STATUS_LABELS = {
  pending: 'In attesa', confirmed: 'Confermato', shipped: 'Spedito',
  delivered: 'Consegnato', cancelled: 'Annullato', invoiced: 'Fatturato',
};
const ORDER_STATUS_ICONS = {
  pending: AlertCircle, confirmed: Package, shipped: Truck,
  delivered: CheckSquare, cancelled: AlertCircle, invoiced: Activity,
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatEuro = (v) => {
  if (!v) return '€0';
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `€${(v / 1_000).toFixed(0)}k`;
  return `€${Math.round(v)}`;
};

const formatTime = (iso) => {
  if (!iso || !iso.includes('T')) return '';
  const t = iso.split('T')[1]?.substring(0, 5);
  return t === '00:00' || t === '23:59' ? '' : t;
};

const todayDate = () => new Date().toISOString().slice(0, 10);

// ── Sub-components ─────────────────────────────────────────────────────────────
function KPICard({ title, value, subtitle, icon: Icon, accent }) {
  return (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className="hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ml-3"
            style={{ background: accent + '18' }}>
            <Icon size={18} style={{ color: accent }} strokeWidth={1.6} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AgendaItem({ item }) {
  const time = formatTime(item.time);
  if (item.kind === 'event') {
    const color = EVENT_COLORS[item.subtype] || '#6366f1';
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0" data-testid="agenda-item-event">
        <div className="flex-shrink-0 w-12 text-right">
          {time ? <span className="text-xs font-mono font-semibold text-muted-foreground">{time}</span>
            : <span className="text-xs text-muted-foreground">Todo</span>}
        </div>
        <div className="w-0.5 self-stretch rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{item.title}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs" style={{ color }}>{EVENT_TYPE_LABELS[item.subtype] || item.subtype}</span>
            {item.location && <span className="text-xs text-muted-foreground truncate">· {item.location}</span>}
          </div>
        </div>
      </div>
    );
  }
  if (item.kind === 'activity') {
    const color = PRIORITY_COLORS[item.priority] || '#06b6d4';
    const Icon = ACT_TYPE_ICONS[item.subtype] || CheckSquare;
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0" data-testid="agenda-item-activity">
        <div className="flex-shrink-0 w-12 flex justify-end pt-0.5">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: color + '20' }}>
            <Icon size={13} style={{ color }} />
          </div>
        </div>
        <div className="w-0.5 self-stretch rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{item.title}</p>
          <span className="text-xs" style={{ color }}>
            {PRIORITY_LABELS[item.priority]} · Attività
          </span>
        </div>
      </div>
    );
  }
  if (item.kind === 'opportunity') {
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0" data-testid="agenda-item-opportunity">
        <div className="flex-shrink-0 w-12 flex justify-end pt-0.5">
          <div className="w-6 h-6 rounded-md flex items-center justify-center bg-amber-500/15">
            <TrendingUp size={13} className="text-amber-600" />
          </div>
        </div>
        <div className="w-0.5 self-stretch rounded-full flex-shrink-0 bg-amber-500" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{item.title}</p>
          <span className="text-xs text-amber-600">
            Chiusura prevista oggi · {formatEuro(item.value)}{item.company_name ? ` · ${item.company_name}` : ''}
          </span>
        </div>
      </div>
    );
  }
  if (item.kind === 'order') {
    const color = ORDER_STATUS_COLORS[item.status] || '#475569';
    const StatusIcon = ORDER_STATUS_ICONS[item.status] || Package;
    const isDeliveryToday = item.expected_delivery === todayDate();
    return (
      <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0" data-testid="agenda-item-order">
        <div className="flex-shrink-0 w-12 flex justify-end pt-0.5">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: color + '20' }}>
            <StatusIcon size={13} style={{ color }} />
          </div>
        </div>
        <div className="w-0.5 self-stretch rounded-full flex-shrink-0" style={{ background: color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-tight">{item.title}</p>
          <span className="text-xs" style={{ color }}>
            {ORDER_STATUS_LABELS[item.status] || item.status}
            {item.company_name ? ` · ${item.company_name}` : ''}
            {isDeliveryToday ? ' · Consegna oggi!' : ''}
            {item.total ? ` · ${formatEuro(item.total)}` : ''}
          </span>
        </div>
      </div>
    );
  }
  return null;
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('annual');
  const [year, setYear]         = useState(new Date().getFullYear());
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = viewMode === 'annual' ? { year } : {};
      const res = await axios.get(`${API}/admin/dashboard`, { params });
      setStats(res.data);
    } catch (e) {
      console.error('Dashboard error:', e);
    } finally {
      setLoading(false);
    }
  }, [viewMode, year]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  const agenda = stats?.today_agenda || [];
  const eventsToday      = agenda.filter(i => i.kind === 'event');
  const activitiesToday  = agenda.filter(i => i.kind === 'activity');
  const oppsToday        = agenda.filter(i => i.kind === 'opportunity');
  const ordersToday      = agenda.filter(i => i.kind === 'order');

  const pipelineData = Object.entries(stats?.pipeline_by_stage || {})
    .filter(([k]) => k !== 'lost')
    .map(([key, data]) => ({
      name: STAGE_LABELS[key] || key,
      value: Math.round(data.value || 0),
      count: data.count || 0,
      color: STAGE_COLORS[key] || '#6366f1',
    }));

  const availableYears = (stats?.available_years || [String(year)])
    .map(Number).sort((a, b) => a - b);
  const minYear = Math.min(...availableYears, year - 2);
  const maxYear = Math.max(...availableYears, year);
  const selectableYears = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);

  const today     = todayDate();
  const dayName   = new Date().toLocaleDateString('it-IT', { weekday: 'long' });
  const dayFull   = new Date().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  const yearLabel = viewMode === 'annual' ? `Anno ${year}` : 'Storico cumulativo';

  const kpiSubLabel = viewMode === 'annual' ? `Anno ${year}` : 'Tutto il periodo';

  return (
    <div className="space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold capitalize" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Buongiorno, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-0.5 capitalize">{dayName}, {dayFull}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* View mode toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted border border-border" data-testid="dash-view-toggle">
            <button
              onClick={() => setViewMode('annual')}
              data-testid="dash-annual-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'annual' ? 'bg-card shadow-sm text-foreground ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Calendar size={13} />Vista Annuale
            </button>
            <button
              onClick={() => setViewMode('cumulative')}
              data-testid="dash-cumulative-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                viewMode === 'cumulative' ? 'bg-card shadow-sm text-foreground ring-1 ring-border' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Database size={13} />Cumulativo
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats} data-testid="refresh-dashboard">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* ── Year selector (annual mode only) ─────────────────────────────────── */}
      {viewMode === 'annual' && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Anno:</span>
          <button onClick={() => setYear(y => y - 1)} className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors">
            <ChevronLeft size={14} />
          </button>
          <div className="flex gap-1 flex-wrap">
            {selectableYears.map(y => {
              const hasData = availableYears.includes(y);
              return (
                <button key={y} onClick={() => setYear(y)} data-testid={`dash-year-${y}`}
                  className={`px-3 py-1 rounded-lg text-sm font-semibold border transition-all ${
                    y === year ? 'bg-primary text-primary-foreground border-primary'
                      : hasData ? 'border-border hover:bg-muted'
                      : 'border-dashed border-border text-muted-foreground hover:bg-muted/50'
                  }`}>
                  {y}
                </button>
              );
            })}
          </div>
          <button onClick={() => setYear(y => y + 1)} className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors">
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* ── TODAY PANEL ────────────────────────────────────────────────────── */}
      <Card className="border-2 border-primary/20 bg-primary/[0.03]" data-testid="today-panel">
        <CardHeader className="pb-3 pt-4 px-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center">
                <Zap size={16} className="text-primary" />
              </div>
              <div>
                <CardTitle className="text-base font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Da fare oggi
                </CardTitle>
                <p className="text-xs text-muted-foreground">{agenda.length} {agenda.length === 1 ? 'elemento' : 'elementi'} in programma</p>
              </div>
            </div>
            {agenda.length > 0 && (
              <div className="flex gap-2">
                {eventsToday.length > 0 && (
                  <Badge variant="outline" className="text-xs gap-1 border-indigo-500/30 text-indigo-600 dark:text-indigo-400">
                    <Calendar size={10} />{eventsToday.length} {eventsToday.length === 1 ? 'evento' : 'eventi'}
                  </Badge>
                )}
                {activitiesToday.length > 0 && (
                  <Badge variant="outline" className="text-xs gap-1 border-cyan-500/30 text-cyan-600 dark:text-cyan-400">
                    <CheckSquare size={10} />{activitiesToday.length} {activitiesToday.length === 1 ? 'attività' : 'attività'}
                  </Badge>
                )}
                {ordersToday.length > 0 && (
                  <Badge variant="outline" className="text-xs gap-1 border-sky-500/30 text-sky-600 dark:text-sky-400">
                    <ShoppingCart size={10} />{ordersToday.length} {ordersToday.length === 1 ? 'ordine' : 'ordini'}
                  </Badge>
                )}
                {oppsToday.length > 0 && (
                  <Badge variant="outline" className="text-xs gap-1 border-amber-500/30 text-amber-600">
                    <TrendingUp size={10} />{oppsToday.length} chiusure
                  </Badge>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          {agenda.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center mb-3">
                <CheckSquare size={22} className="text-green-500" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">Nessun impegno per oggi</p>
              <p className="text-xs text-muted-foreground mt-1">Ottimo momento per pianificare nuove attività</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => navigate('/calendar')}>
                  <Calendar size={13} className="mr-1.5" />Calendario
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/activities')}>
                  <CheckSquare size={13} className="mr-1.5" />Attività
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-0 md:gap-5">
              {/* Events column */}
              {eventsToday.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 mb-2 flex items-center gap-1.5">
                    <Calendar size={11} />Calendario ({eventsToday.length})
                  </p>
                  {eventsToday.map(item => <AgendaItem key={item.id} item={item} />)}
                </div>
              )}

              {/* Activities column */}
              {activitiesToday.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-cyan-500 mb-2 flex items-center gap-1.5">
                    <CheckSquare size={11} />Attività ({activitiesToday.length})
                  </p>
                  {activitiesToday.map(item => <AgendaItem key={item.id} item={item} />)}
                </div>
              )}

              {/* Orders column */}
              {ordersToday.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-sky-500 mb-2 flex items-center gap-1.5">
                    <ShoppingCart size={11} />Ordini ({ordersToday.length})
                  </p>
                  {ordersToday.map(item => <AgendaItem key={item.id} item={item} />)}
                </div>
              )}

              {/* Opportunities column */}
              {oppsToday.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-500 mb-2 flex items-center gap-1.5">
                    <TrendingUp size={11} />Chiusure ({oppsToday.length})
                  </p>
                  {oppsToday.map(item => <AgendaItem key={item.id} item={item} />)}
                </div>
              )}

              {/* Quick actions if only 1 column has data */}
              {(eventsToday.length + activitiesToday.length + ordersToday.length + oppsToday.length) < 2 && (
                <div className="flex flex-col gap-2 pt-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Azioni rapide</p>
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => navigate('/calendar')}>
                    <Calendar size={13} className="mr-2 text-indigo-500" />Calendario
                  </Button>
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => navigate('/activities')}>
                    <CheckSquare size={13} className="mr-2 text-cyan-500" />Attività
                  </Button>
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => navigate('/orders')}>
                    <ShoppingCart size={13} className="mr-2 text-sky-500" />Ordini
                  </Button>
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => navigate('/pipeline')}>
                    <TrendingUp size={13} className="mr-2 text-amber-500" />Pipeline
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard title="Pipeline Attiva"   value={formatEuro(stats?.pipeline_value)}   subtitle={`${stats?.open_opportunities || 0} opportunità aperte`}   icon={TrendingUp}   accent="#22c55e" />
        <KPICard title="Ordini"            value={formatEuro(stats?.orders_revenue)}   subtitle={`${stats?.orders_count || 0} ordini · ${kpiSubLabel}`}      icon={ShoppingCart} accent="#0ea5e9" />
        <KPICard title="Tasso Conversione" value={`${stats?.conversion_rate || 0}%`}  subtitle={kpiSubLabel}                                                 icon={Activity}     accent="#a855f7" />
        <KPICard title="Nuovi Lead"        value={stats?.new_leads || 0}              subtitle={viewMode === 'annual' ? `Anno ${year}` : 'Ultimi 30 giorni'} icon={Target}       accent="#f59e0b" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard title="Aziende"         value={stats?.companies_count || 0}          subtitle="Nel CRM"          icon={Building2}   accent="#6366f1" />
        <KPICard title="Contatti"        value={stats?.contacts_count || 0}           subtitle="Nel CRM"          icon={Users}       accent="#3b82f6" />
        <KPICard title="Ordini Mese"     value={formatEuro(stats?.orders_month_revenue)} subtitle={`${stats?.orders_this_month || 0} ordini questo mese`} icon={Package} accent="#06b6d4" />
        <KPICard title="Attività Aperte" value={stats?.pending_activities || 0}       subtitle="Da completare"    icon={CheckSquare} accent="#f97316" />
      </div>

      {/* ── Charts Row ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left chart: Trend mensile (annual) OR Trend annuale (cumulative) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {viewMode === 'annual' ? `Andamento Mensile — ${year}` : 'Andamento Annuale (storico)'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {viewMode === 'annual' ? (
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={stats?.monthly_trend || []} margin={{ left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={v => `€${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(v, name) => [name === 'fatturato' ? formatEuro(v) : v, name === 'fatturato' ? 'Fatturato' : 'N° Ordini']}
                  />
                  <Legend formatter={n => n === 'fatturato' ? 'Fatturato' : 'N° Ordini'} iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="fatturato" fill="hsl(145, 45%, 28%)" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="right" dataKey="ordini" fill="#0ea5e9" radius={[3, 3, 0, 0]} opacity={0.7} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={190}>
                <LineChart data={stats?.annual_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `€${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
                  <Tooltip formatter={(v, name) => [name === 'fatturato' ? formatEuro(v) : v, name === 'fatturato' ? 'Fatturato' : 'N° Ordini']} />
                  <Line type="monotone" dataKey="fatturato" stroke="hsl(145, 45%, 28%)" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Pipeline by stage */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Pipeline per Fase
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate('/pipeline')}>
                Vedi tutto <ArrowRight size={12} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {pipelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={190}>
                <BarChart data={pipelineData} layout="vertical" margin={{ left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `€${v / 1000}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={78} />
                  <Tooltip formatter={(v, n, p) => [formatEuro(v), `${p.payload.count} opp.`]} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {pipelineData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center flex-col gap-2">
                <TrendingUp size={28} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Nessuna opportunità</p>
                <Button variant="outline" size="sm" onClick={() => navigate('/pipeline')}>
                  Aggiungi Opportunità
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Bottom Row: Top Opps + Recent Orders ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Opportunities */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Top Opportunità per Valore
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/pipeline')}>
                <ArrowRight size={14} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(stats?.top_opportunities || []).length > 0 ? (
              <div className="space-y-2.5" data-testid="top-opportunities">
                {stats.top_opportunities.map(opp => {
                  const isCloseToday = opp.expected_close === today;
                  const isOverdue = opp.expected_close && opp.expected_close < today;
                  return (
                    <div key={opp.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate('/pipeline')}>
                      <div className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: STAGE_COLORS[opp.stage] || '#6366f1' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{opp.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{opp.company_name}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-primary">{formatEuro(opp.value)}</p>
                        {opp.expected_close && (
                          <p className={`text-xs ${isCloseToday ? 'text-amber-500 font-semibold' : isOverdue ? 'text-red-500' : 'text-muted-foreground'}`}>
                            {isCloseToday ? 'Oggi!' : isOverdue ? 'Scaduta' : new Date(opp.expected_close).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <TrendingUp size={28} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nessuna opportunità aperta</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/pipeline')}>
                  Crea Opportunità
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Ordini Recenti
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7" onClick={() => navigate('/orders')}>
                <ArrowRight size={14} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(stats?.recent_orders || []).length > 0 ? (
              <div className="space-y-2.5" data-testid="recent-orders">
                {stats.recent_orders.map(ord => (
                  <div key={ord.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/orders')}>
                    <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
                      style={{ background: (ORDER_STATUS_COLORS[ord.status] || '#475569') + '18' }}>
                      <ShoppingCart size={14} style={{ color: ORDER_STATUS_COLORS[ord.status] || '#475569' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{ord.order_number}</p>
                      <p className="text-xs text-muted-foreground truncate">{ord.company_name}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold">{formatEuro(ord.total_amount)}</p>
                      <Badge variant="outline" className="text-[10px] mt-0.5"
                        style={{ color: ORDER_STATUS_COLORS[ord.status], borderColor: ORDER_STATUS_COLORS[ord.status] + '50' }}>
                        {ORDER_STATUS_LABELS[ord.status] || ord.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <ShoppingCart size={28} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Nessun ordine ancora</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/orders')}>
                  Crea Ordine
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
